import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/store';

/**
 * Resolves the tenant this session belongs to.
 *
 * Two paths, because the backend hands the two roles different things at
 * login: a driver's login response carries tenant_id directly, while an
 * admin's does not and has to discover it from GET /api/tenants (which is
 * already scoped to the caller's key — it returns that admin's tenant, not
 * every tenant on the installation). Once known it is cached in the auth
 * store so a reload does not spend a round-trip rediscovering it.
 */
export function useTenantId() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const apiKey = useAuthStore((s) => s.apiKey);
  const setTenantId = useAuthStore((s) => s.setTenantId);

  const { data } = useQuery({
    queryKey: qk.tenants(),
    queryFn: () => api('/api/tenants'),
    enabled: Boolean(apiKey) && !tenantId,
    staleTime: Infinity,
  });

  useEffect(() => {
    // GET /api/tenants is already scoped to the caller's own tenant and
    // returns it as a one-element list (kept a list for backward
    // compatibility) — hence tenants[0], not a search.
    if (tenantId) return;
    const first = Array.isArray(data) ? data[0] : null;
    if (first?.tenant_id) setTenantId(first.tenant_id);
  }, [data, tenantId, setTenantId]);

  return tenantId;
}

/**
 * The tenant row itself — VAT rate, dwell policy, cost assumptions.
 *
 * Read from the LIST endpoint, not `GET /api/tenants/<id>`: that path only
 * accepts PUT (see blueprints/tenants.py), so asking for it returns 405 and
 * the settings form silently renders empty. GET /api/tenants already returns
 * this tenant's full row, so this is also one request rather than two.
 */
export function useTenant() {
  const apiKey = useAuthStore((s) => s.apiKey);
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.tenants(),
    queryFn: () => api('/api/tenants'),
    enabled: Boolean(apiKey),
    staleTime: 60_000,
    select: (rows) =>
      (Array.isArray(rows) ? rows : []).find((row) => row.tenant_id === tenantId) ?? rows?.[0] ?? null,
  });
}

export function useDashboard() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.dashboard(tenantId),
    queryFn: () => api(`/api/tenants/${tenantId}/dashboard`),
    enabled: Boolean(tenantId),
  });
}

/**
 * The open dwell stops that need somebody, keyed for O(1) lookup by both
 * order and driver — the map marks the truck, the orders table marks the
 * row, and neither should re-scan an array per render.
 *
 * Two kinds of row arrive together and `state` tells them apart. 'exceeding'
 * is a truck standing at a site right now. 'unclosed' is a stop that has
 * been open longer than the engine's plausibility bound (dwellCapHours,
 * sent by the server rather than hard-coded here): nobody closed the record,
 * the truck left at some unknown earlier time, and until this split existed
 * the console showed that as the same red "+3,780 min over allowance" tag,
 * refreshed every sixty seconds forever (P1-26). `unclosed` is broken out
 * because it is a worklist — every row in it needs a human to force-close
 * it. `exceeding` is broken out (V-307) for the opposite reason: a caller
 * that wants "over allowance right now" must never be handed the raw,
 * unfiltered `rows` and left to re-derive the same split itself.
 */
export function useDwellStatus() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.dwellStatus(tenantId),
    queryFn: async () => {
      const body = await api(`/api/tenants/${tenantId}/dwell-status`);
      const rows = body?.rows ?? [];
      const byOrder = {};
      const byDriver = {};
      rows.forEach((row) => {
        if (row.order_id) byOrder[row.order_id] = row;
        if (row.driver_id) byDriver[row.driver_id] = row;
      });
      return {
        rows,
        byOrder,
        byDriver,
        // V-307. Broken out for the same reason `unclosed` is: a caller
        // that wants "trucks over their allowance right now" must not get
        // handed `rows` and left to remember the filter itself -- Command
        // Center's FleetStatusRings/IncidentFeed didn't, and both ended up
        // treating an `unclosed` row (no measurement, excess_minutes is
        // null) as if it were a live `exceeding` one.
        exceeding: rows.filter((row) => row.state === 'exceeding'),
        unclosed: rows.filter((row) => row.state === 'unclosed'),
        // Null, not 12: the bound belongs to the engine, and a console that
        // invents its own would keep quoting the old one after somebody
        // changed it.
        dwellCapHours: body?.dwell_cap_hours ?? null,
      };
    },
    enabled: Boolean(tenantId),
    placeholderData: {
      rows: [], byOrder: {}, byDriver: {}, exceeding: [], unclosed: [], dwellCapHours: null,
    },
  });
}

/**
 * Force-closes one stuck-open dwell record. The only resolution an
 * 'unclosed' row has: the backend stamps departed_trigger='admin_override'
 * so the Prastovų Aktas keeps the stop out of every money total and reports
 * it among the excluded ones instead — closing it cannot invoice anybody.
 */
export function useForceCloseDwellRecord() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dwellRecordId) =>
      api(`/api/dwell-records/${dwellRecordId}/close`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.dwellStatus(tenantId) });
      queryClient.invalidateQueries({ queryKey: ['orders', tenantId] });
    },
  });
}

/**
 * GET /api/tenants/<id>/setup — the six things that have to be true before
 * this install's numbers mean anything (P1-2). Four are counts, two are
 * "has a human answered this money question", which is not the same as
 * "does a value exist": tenants.fuel_cost_per_km ships at 0.35 EUR/km and
 * multiplies into every margin the Analytics tab shows. See
 * core/setup_status.py.
 *
 * Refetched on window focus and on every live change, because the whole
 * point is that it disappears the moment the last step is finished — a
 * checklist still showing "add a driver" after the dispatcher added one, in
 * another tab or on the phone, is worse than no checklist. Cheap enough to
 * do that with: six COUNT(*)s on indexed tenant_id columns.
 */
export function useSetupStatus() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.setupStatus(tenantId),
    queryFn: () => api(`/api/tenants/${tenantId}/setup`),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}
